import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Handle refresh token errors by clearing invalid session
  const handleAuthError = async (error) => {
    if (error?.message?.includes('refresh') || error?.message?.includes('Refresh Token')) {
      console.warn('Invalid refresh token detected, clearing session...')
      try {
        // Clear the invalid session from Supabase
        await supabase?.auth?.signOut()
        // Clear local state
        setUser(null)
        profileOperations?.clear()
      } catch (clearError) {
        console.error('Error clearing invalid session:', clearError)
      }
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check with error handling
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (error) {
        handleAuthError(error)
      } else {
        authStateHandlers?.onChange(null, session)
      }
    })?.catch((error) => {
      console.error('Session initialization error:', error)
      handleAuthError(error)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        // Check for auth errors in state changes
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('Token refresh failed, session is null')
          handleAuthError({ message: 'refresh token failed' })
        } else if (event === 'SIGNED_OUT') {
          profileOperations?.clear()
        }
        authStateHandlers?.onChange(event, session)
      }
    )

    // Listen for storage events to detect manual session clearing
    const handleStorageChange = (e) => {
      if (e?.key?.includes('supabase.auth.token') && !e?.newValue) {
        console.warn('Auth token removed from storage, clearing session...')
        setUser(null)
        profileOperations?.clear()
      }
    }

    window?.addEventListener('storage', handleStorageChange)

    return () => {
      subscription?.unsubscribe()
      window?.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      if (error) {
        handleAuthError(error)
      }
      return { data, error }
    } catch (error) {
      handleAuthError(error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, fullName, role = 'student', inviteCode = '') => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role,
            invite_code: inviteCode
          }
        }
      })
      if (error) {
        handleAuthError(error)
      }
      return { data, error }
    } catch (error) {
      handleAuthError(error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      } else {
        handleAuthError(error)
      }
      return { error }
    } catch (error) {
      handleAuthError(error)
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}